"use client"

import { FormikProvider, useFormik } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAssignKnowledgeBaseFolderMutation } from "@/hooks/mutations/knowledge-base-folders"
import type {
  KnowledgeBaseAgentRef,
  KnowledgeBaseFolderView,
} from "@/server-actions/knowledge-base-folders"
import { useTranslations } from "next-intl"


const AGENT_TYPE_FULL_LABELS: Record<KnowledgeBaseAgentRef["type"], keyof typeof import("@/translations/en/ai.json").Ai.AiKnowledgeBaseFolderAssignmentMenu> = {
  email: "emailAgent",
  message: "messageAgent",
  voice: "voiceAgent",
}

type FolderAssignmentFormValues = {
  folderId: string
  agentType: KnowledgeBaseAgentRef["type"]
  agentId: string
  assign: boolean
}

type KnowledgeBaseFolderAssignmentMenuProps = {
  folder: KnowledgeBaseFolderView
  agents: KnowledgeBaseAgentRef[]
  onUpdated?: () => void | Promise<void>
}

export function KnowledgeBaseFolderAssignmentMenu({
  folder,
  agents,
  onUpdated,
}: KnowledgeBaseFolderAssignmentMenuProps) {
  const tUi = useTranslations("Ai.AiKnowledgeBaseFolderAssignmentMenu")

  const assignFolder = useAssignKnowledgeBaseFolderMutation({
    onSuccess: async () => {
      await onUpdated?.()
    },
  })

  const formik = useFormik<FolderAssignmentFormValues>({
    initialValues: {
      folderId: folder.id,
      agentType: "voice",
      agentId: "",
      assign: true,
    },
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        const agent = agents.find(item => item.id === values.agentId && item.type === values.agentType)
        await assignFolder.mutateAsync({
          ...values,
          agentName: agent?.name,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling already shows the user-facing toast and maps validation errors.
      }
    },
  })

  const submitAssignment = async (agent: KnowledgeBaseAgentRef, assign: boolean) => {
    await formik.setValues({
      folderId: folder.id,
      agentType: agent.type,
      agentId: agent.id,
      assign,
    })
    await formik.submitForm()
  }

  if (assignFolder.isPending) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  }

  return (
    <FormikProvider value={formik}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={agents.length === 0} className="h-12 w-full text-base sm:h-9 sm:w-auto sm:text-sm">
            <span key={tUi("assignAgent")}>{tUi("assignAgent")}</span></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-h-[min(20rem,calc(100dvh-8rem))] w-[calc(100vw-2rem)] max-w-md sm:w-96"
        >
          {agents.length === 0 ? (
            <DropdownMenuItem disabled><span key={tUi("noAgentsAvailable")}>{tUi("noAgentsAvailable")}</span></DropdownMenuItem>
          ) : (
            agents.map(agent => {
              const isAssigned = folder.agents.some(assigned => assigned.id === agent.id && assigned.type === agent.type)
              return (
                <DropdownMenuItem
                  key={`${agent.type}-${agent.id}`}
                  className="min-w-0 items-start gap-3 whitespace-normal break-words py-2"
                  onClick={() => void submitAssignment(agent, !isAssigned)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{agent.name}</span>
                    <span key={tUi(AGENT_TYPE_FULL_LABELS[agent.type])} className="block text-xs text-muted-foreground">{tUi(AGENT_TYPE_FULL_LABELS[agent.type])}</span>
                  </span>
                  <span key={isAssigned ? tUi("unassign") : tUi("assign")} className="max-w-[45%] text-right text-xs">{isAssigned ? tUi("unassign") : tUi("assign")}</span>
                </DropdownMenuItem>
              )
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </FormikProvider>
  )
}
